import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  useToast,
  VStack,
  FormErrorMessage,
  HStack,
  Stack,
  Switch,
  Text,
  Badge,
} from "@chakra-ui/react";
import {
  LicenseModel,
  TenantBasicDetailModel,
  LicenseType,
  renderLicenseIcon,
} from "../Tenants/TenantModel";
import {
  UpdateTenantLicense,
  CreateTenantLicense,
} from "../../../services/SuperAdminServices/TenantService";
import { useAppContext } from "../../../context/AppContext";

interface TenantLicenseDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  licenseDetail?: LicenseModel;
  tenantDetails: TenantBasicDetailModel;
  licenseIndex?: number;
}

const TenantLicenseDetailsModal: React.FC<TenantLicenseDetailsProps> = ({
  isOpen,
  onClose,
  onSave,
  licenseDetail,
  tenantDetails,
  licenseIndex,
}) => {
  const isEdit = typeof licenseDetail !== "undefined";
  const toast = useToast();
  const { accessToken } = useAppContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    noOfUsers: "",
    maxToken: "",
    assignedLicenses: "",
    availableLicenses: "",
    dateRange: "", // Added error field for date validation
  });
  const today = new Date();
  const [isPastStart, setIsPastStart] = useState(false);

  const [formData, setFormData] = useState<LicenseModel>({
    LicenseType: LicenseType.Pro,
    NoOfUsers: 0,
    LicenseStartDate: new Date(),
    LicenseEndDate: new Date(),
    TenantLicenseID: 0,
    MaxTokenPerMonth: null,
    LMAndBotTokens: null,
    assignedLicenses: 0,
    availableLicenses: 0,
    isLicenseActive: false,
    title: "",
  });

  useEffect(() => {
    if (isEdit) {
      // ——— edit mode: populate from licenseDetail ———
      const startDate = new Date(licenseDetail!.LicenseStartDate);
      const endDate = new Date(licenseDetail!.LicenseEndDate);

      // Check if start date is in the past
      const isStartDatePassed = (today >=  startDate && today <= endDate);
      setIsPastStart(isStartDatePassed);

      setFormData({
        LicenseType: licenseDetail!.LicenseType,
        NoOfUsers: licenseDetail!.NoOfUsers,
        LicenseStartDate: startDate,
        LicenseEndDate: endDate,
        TenantLicenseID: licenseDetail!.TenantLicenseID,
        MaxTokenPerMonth: licenseDetail!.MaxTokenPerMonth,
        LMAndBotTokens: licenseDetail!.LMAndBotTokens,
        availableLicenses: licenseDetail!.availableLicenses,
        assignedLicenses: licenseDetail!.assignedLicenses,
        isLicenseActive: licenseDetail!.isLicenseActive,
        title: licenseDetail!.title,
      });
    } else {
      // ——— add mode: reset to defaults ———
      setIsPastStart(false); // New licenses are always editable
      setFormData({
        LicenseType: LicenseType.Pro,
        NoOfUsers: 0,
        LicenseStartDate: new Date(),
        LicenseEndDate: new Date(),
        TenantLicenseID: 0,
        MaxTokenPerMonth: null,
        LMAndBotTokens : null,
        availableLicenses: 0,
        assignedLicenses: 0,
        isLicenseActive: false,
        title: "",
      });
    }
    setErrors({
      noOfUsers: "",
      maxToken: "",
      assignedLicenses: "",
      availableLicenses: "",
      dateRange: "", // Reset date validation error
    });
  }, [isOpen, licenseIndex]);

  function formatLocalDate(d: Date): string {
    const Y = d.getFullYear();
    const M = String(d.getMonth() + 1).padStart(2, "0");
    const D = String(d.getDate()).padStart(2, "0");
    return `${Y}-${M}-${D}`;
  }

  const handleNumberChange = (
    name: keyof LicenseModel,
    _str: string,
    num: number
  ) => {
    let fieldError = "";
    if (isNaN(num) || num < 0) {
      fieldError = "Value must be a non-negative number";
    }
    setErrors((prev) => ({
      ...prev,
      [name === "NoOfUsers" ? "noOfUsers" : "maxToken"]: fieldError,
    }));
    setFormData((prev) => ({ ...prev, [name]: num }));
  };

  // Validate dates when either date changes
  const validateDates = (
    startDate: Date,
    endDate: Date
  ): string => {
    if (startDate >= endDate) {
      return "Start date must be before end date";
    }
    return "";
  };

  const handleDateChange = (
    field: "LicenseStartDate" | "LicenseEndDate",
    dateString: string
  ) => {
    const [Y, M, D] = dateString.split("-").map(Number);
    const utc = new Date(Date.UTC(Y, M - 1, D));

    if (field === "LicenseStartDate") {
      // If changing start date, check if it's in the future
      const isNewDateInFuture = utc > today;
      // Only update isPastStart if in edit mode and this is a modification of the start date
      if (isEdit) {
        setIsPastStart(!isNewDateInFuture);
      }
      
      // Update form data and validate dates
      const newFormData = { ...formData, [field]: utc };
      setFormData(newFormData);
      const dateError = validateDates(utc, newFormData.LicenseEndDate);
      setErrors(prev => ({ ...prev, dateRange: dateError }));
    } else {
      // End date is changing
      const newFormData = { ...formData, [field]: utc };
      setFormData(newFormData);
      const dateError = validateDates(newFormData.LicenseStartDate, utc);
      setErrors(prev => ({ ...prev, dateRange: dateError }));
    }
  };

  const handleSubmit = async () => {
    // Validate dates first
    const dateError = validateDates(formData.LicenseStartDate, formData.LicenseEndDate);
    if (dateError) {
      setErrors(prev => ({ ...prev, dateRange: dateError }));
      return;
    }
    
    // simple client-side validation
    if (formData.NoOfUsers <= 0) {
      setErrors((e) => ({
        ...e,
        noOfUsers: "No of users must be greater than zero",
      }));
      return;
    }
    if (
      formData.LicenseType === LicenseType.Enterprise &&
      (!formData.MaxTokenPerMonth || formData.MaxTokenPerMonth <= 0)
    ) {
      setErrors((e) => ({
        ...e,
        maxToken: "Max tokens must be > 0 for Enterprise",
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEdit) {
        // ——— existing update flow ———
        await UpdateTenantLicense(
          {
            ...tenantDetails,
            TenantLicenseID: licenseDetail!.TenantLicenseID,
            LicenseType: formData.LicenseType,
            NoOfUsers: formData.NoOfUsers,
            LicenseStartDate: formData.LicenseStartDate,
            LicenseEndDate: formData.LicenseEndDate,
            MaxTokenPerMonth: formData.MaxTokenPerMonth,
            LMAndBotTokens: formData.LMAndBotTokens,
            assignedLicenses: formData.assignedLicenses,
            availableLicenses: formData.availableLicenses,
            isLicenseActive: formData.isLicenseActive,
            title: formData.title,
          },
          accessToken
        );
        toast({
          position: "top",
          title: "License details updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // // ——— NEW: call your "add license" service ———
        await CreateTenantLicense(
          {
            ...tenantDetails,
            TenantLicenseID: 0,
            LicenseType: formData.LicenseType,
            NoOfUsers: formData.NoOfUsers,
            LicenseStartDate: formData.LicenseStartDate,
            LicenseEndDate: formData.LicenseEndDate,
            MaxTokenPerMonth: formData.MaxTokenPerMonth,
            LMAndBotTokens: formData.LMAndBotTokens,
            assignedLicenses: formData.assignedLicenses,
            availableLicenses: formData.availableLicenses,
            isLicenseActive: formData.isLicenseActive,
            title: formData.title,
          },
          accessToken
        );
        toast({
          position: "top",
          title: " License details added successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
      onSave();
      onClose();
    } catch (error) {
      console.error("Error updating license:", error);
      toast({
        position: "top",
        title: "Error updating license details",
        description: "Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEdit ? "Edit License Details" : "Add New License"}
          {isEdit && isPastStart && (
            <Badge colorScheme="orange" ml={2}>
              Limited Edit Mode
            </Badge>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isEdit && isPastStart && (
            <Text color="orange.500" mb={4}>
              This license has already started. Some fields cannot be modified.
            </Text>
          )}
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>License Type</FormLabel>
              <HStack>
                <Select
                  value={formData.LicenseType}
                  isDisabled={isEdit}
                  fontWeight="bold"
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      LicenseType: Number(e.target.value),
                      MaxTokenPerMonth:
                        Number(e.target.value) === LicenseType.Enterprise
                          ? f.MaxTokenPerMonth
                          : null,
                    }))
                  }
                >
                  {/* <option value={LicenseType.Basic}>Basic</option> */}
                  <option value={LicenseType.Pro}>Pro</option>
                  <option value={LicenseType.Enterprise}>Enterprise</option>
                </Select>
                {renderLicenseIcon(formData.LicenseType)}
              </HStack>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, title: e.target.value }))
                }
                isDisabled={isPastStart}
                fontWeight="bold"
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.noOfUsers}>
              <FormLabel>Total License</FormLabel>
              <NumberInput
                value={formData.NoOfUsers}
                min={0}
                onChange={(s, n) => handleNumberChange("NoOfUsers", s, n)}
                isDisabled={isPastStart}
                fontWeight="bold"
              >
                <NumberInputField
                  name="NoOfUsers"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  fontWeight="bold"
                />
              </NumberInput>
              <FormErrorMessage>{errors.noOfUsers}</FormErrorMessage>
            </FormControl>

            {/* only show used/available when editing */}
            {isEdit && (
              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Used License</FormLabel>
                  <NumberInput
                    value={formData.assignedLicenses}
                    isDisabled
                    fontWeight="bold"
                  >
                    <NumberInputField fontWeight="bold" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Available License</FormLabel>
                  <NumberInput
                    value={formData.availableLicenses}
                    isDisabled
                    fontWeight="bold"
                  >
                    <NumberInputField
                      fontWeight="bold"
                      color={
                        formData.availableLicenses > 0 ? "green.500" : "red.500"
                      }
                    />
                  </NumberInput>
                </FormControl>
              </HStack>
            )}

            {(formData.LicenseType === LicenseType.Enterprise || formData.LicenseType === LicenseType.Pro) && (
              <FormControl isRequired isInvalid={!!errors.maxToken}>
                <FormLabel>Max Token Per Month (1 Credit = 500 Token)</FormLabel>
                <NumberInput
                  value={formData.MaxTokenPerMonth ?? 0}
                  min={0}
                  onChange={(s, n) =>
                    handleNumberChange("MaxTokenPerMonth", s, n)
                  }
                  isDisabled={isPastStart}
                  fontWeight="bold"
                >
                  <NumberInputField
                    name="MaxTokenPerMonth"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </NumberInput>
                <FormErrorMessage>{errors.maxToken}</FormErrorMessage>
              </FormControl>
            )}

             {(formData.LicenseType === LicenseType.Enterprise || formData.LicenseType === LicenseType.Pro) && (
              <FormControl isRequired isInvalid={!!errors.maxToken}>
                <FormLabel>Language Model & Bot Tokens</FormLabel>
                <NumberInput
                  value={formData.LMAndBotTokens ?? 0}
                  min={0}
                  onChange={(s, n) =>
                    handleNumberChange("LMAndBotTokens", s, n)
                  }
                  isDisabled={isPastStart}
                  fontWeight="bold"
                >
                  <NumberInputField
                    name="LMAndBotTokens"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </NumberInput>
              </FormControl>
            )}

            {/* Date range forms with validation error message */}
            <FormControl isInvalid={!!errors.dateRange}>
              <FormLabel>License Start Date</FormLabel>
              <Input
                type="date"
                name="LicenseStartDate"
                value={formatLocalDate(formData.LicenseStartDate)}
                onChange={(e) =>
                  handleDateChange("LicenseStartDate", e.target.value)
                }
                isDisabled={isPastStart}
                fontWeight="bold"
              />
            </FormControl>

            <FormControl isInvalid={!!errors.dateRange}>
              <FormLabel>License End Date</FormLabel>
              <Input
                type="date"
                name="LicenseEndDate"
                value={formatLocalDate(formData.LicenseEndDate)}
                onChange={(e) =>
                  handleDateChange("LicenseEndDate", e.target.value)
                }
                fontWeight="bold"
                isDisabled={isPastStart}
              />
              {errors.dateRange && (
                <FormErrorMessage>{errors.dateRange}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <Stack alignItems="left">
                <FormLabel mb={0}>Status</FormLabel>
                <Switch
                  isChecked={formData.isLicenseActive}
                  onChange={(e) => {
                    const isChecked = e.target.checked;

                    if (isPastStart && !isChecked) {
                      // Confirm deactivation
                      const confirmDeactivation = window.confirm(
                        "The license start date has already passed. Are you sure you want to deactivate it?"
                      );
                      if (!confirmDeactivation) return;
                    }

                    setFormData((f) => ({
                      ...f,
                      isLicenseActive: isChecked,
                    }));
                  }}
                  colorScheme="blue"
                />
              </Stack>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText={isEdit ? "Saving" : "Adding"}
            isDisabled={!!errors.dateRange || !!errors.noOfUsers || !!errors.maxToken}
          >
            {isEdit ? "Save" : "Add"}
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TenantLicenseDetailsModal;